import { isCbhApiSuperagentError } from "@src/appV2/lib/utils/errorUtils";
import { z } from "zod";

const conflictSchema = z.object({
  cbhUserId: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  userTypes: z.array(z.string()),
});

const responseSchema = z.object({
  errors: z
    .array(
      z.object({
        code: z.literal("AUTH_IDENTIFIER_OWNED_BY_REAL_WORKER"),
        meta: z.object({
          identifierType: z.enum(["email", "phone_number"]),
          conflict: conflictSchema,
        }),
      })
    )
    .nonempty(),
});

export type ContactConflict = z.infer<typeof conflictSchema>;

export type ParseContactConflictResult =
  | {
      matched: true;
      identifierType: "email" | "phone_number";
      conflict: ContactConflict;
    }
  | { matched: false };

export function parseContactConflict(error: unknown): ParseContactConflictResult {
  // Accept both superagent (status + response.body) and plain shapes used in tests.
  const candidateBody = extractBody(error);
  if (!candidateBody) {
    return { matched: false };
  }

  const parsed = responseSchema.safeParse(candidateBody);
  if (!parsed.success) {
    return { matched: false };
  }

  const first = parsed.data.errors[0];
  return {
    matched: true,
    identifierType: first.meta.identifierType,
    conflict: first.meta.conflict,
  };
}

function extractBody(error: unknown): unknown {
  if (isCbhApiSuperagentError(error)) {
    if (error.status !== 409) {
      return undefined;
    }
    return error.response.body;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: number }).status === 409 &&
    "response" in error
  ) {
    const { response } = error as { response?: { body?: unknown } };
    return response?.body;
  }

  return undefined;
}
